import React, { useState, forwardRef, useImperativeHandle } from 'react';
import {
  TextField,
  Autocomplete,
  CircularProgress,
  Box,
  Typography,
  Paper,
} from '@mui/material';
import { useItemSearch, PharmacyItem } from './itemSearchField.helper';

interface ItemSearchFieldProps {
  onSelectItem: (item: PharmacyItem) => void;
  disabled?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  clinicId?: number;
  deptId?: number | null;
  /** Package ID - when provided, filters items to package items only */
  packageId?: number | null;
  /** Tariff ID - when provided, passes tariff to filter items */
  tariffId?: number | null;
  /** Patient ID - when provided, passes patient context to the API */
  patientId?: number | null;
}

// Add ref type for exposing clearSearch method
export interface ItemSearchFieldRef {
  clearSearch: () => void;
}

const ItemSearchField = forwardRef<ItemSearchFieldRef, ItemSearchFieldProps>(({
  onSelectItem,
  disabled = false,
  onChange,
  clinicId,
  deptId,
  packageId,
  tariffId,
  patientId,
}, ref) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const {
    data: searchResults,
    isLoading,
    isFetching,
  } = useItemSearch(searchTerm, clinicId, deptId, open, packageId, tariffId, patientId);

  // Expose clearSearch method to parent via ref
  useImperativeHandle(ref, () => ({
    clearSearch: () => {
      setInputValue('');
      setSearchTerm('');
      setOpen(false);
      if (onChange) {
        onChange('');
      }
    }
  }));

  const handleInputChange = (_event: React.SyntheticEvent, newValue: string, reason: string) => {
    setInputValue(newValue);

    // When user types, update search term and open dropdown
    if (reason === 'input') {
      setSearchTerm(newValue);
      setOpen(newValue.length > 0);
      if (onChange) {
        onChange(newValue);
      }
      return;
    }

    // When value is cleared (via clear button / ESC), close dropdown
    if (reason === 'clear') {
      setSearchTerm('');
      setOpen(false);
      if (onChange) {
        onChange('');
      }
    }
  };

  const handleSelect = (_event: React.SyntheticEvent, selectedValue: PharmacyItem | null) => {
    if (selectedValue) {
      onSelectItem(selectedValue);
      // Clear the input after selection
      setInputValue('');
      setSearchTerm('');
      setOpen(false);
      if (onChange) {
        onChange('');
      }
    }
  };

  const options = searchResults?.objects || [];

  return (
    <Autocomplete
      open={open}
      onOpen={() => {
        if (inputValue.length > 0) {
          setOpen(true);
        }
      }}
      onClose={() => setOpen(false)}
      disabled={disabled}
      options={options}
      loading={isLoading || isFetching}
      value={null} // Always null so it doesn't persist selected value
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleSelect}
      getOptionLabel={(option) => option.item_name || ''}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      filterOptions={(x) => x} // Disable client-side filtering since we do server-side search
      noOptionsText={
        inputValue.length < 1
          ? 'Type at least 1 character to search...'
          : isLoading || isFetching
            ? 'Searching...'
            : 'No items found'
      }
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search and Add Item"
          size="small"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {(isLoading || isFetching) ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        // Extract key from props to avoid React warning
        // eslint-disable-next-line @typescript-eslint/no-unused-vars, react/prop-types
        const { key, ...otherProps } = props as { key: string; [key: string]: unknown };
        return (
          <Box 
            component="li" 
            {...otherProps} 
            key={option.id} 
            sx={{ 
              py: 1.5,
              px: 2,
              borderBottom: '1px solid #f0f0f0',
              '&:last-child': {
                borderBottom: 'none'
              },
              '&:hover': {
                backgroundColor: '#f8f9fa'
              }
            }}
          >
            <Box sx={{ width: '100%' }}>
              {/* Code and Name on same line with better spacing */}
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: '0.9rem',
                  color: '#2c3e50',
                  lineHeight: 1.5,
                  mb: 0.5
                }}
              >
                <Box 
                  component="span" 
                  sx={{ 
                    fontWeight: 600,
                    color: '#34495e',
                    mr: 1
                  }}
                >
                  {option.item_code}
                </Box>
                <Box component="span" sx={{ color: '#5a6c7d' }}>
                  {option.item_name}
                </Box>
              </Typography>
              
              {/* Batch availability with icon-like design */}
              {option.batches && option.batches.length > 0 && (
                <Box 
                  sx={{ 
                    display: 'inline-flex',
                    alignItems: 'center',
                    backgroundColor: '#e8f5e9',
                    px: 1.5,
                    py: 0.25,
                    borderRadius: 1,
                    mt: 0.5
                  }}
                >
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: '#2e7d32',
                      fontWeight: 500,
                      fontSize: '0.75rem'
                    }}
                  >
                    ✓ {option.batches.length} batch{option.batches.length > 1 ? 'es' : ''} available
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        );
      }}
      PaperComponent={(props) => (
        <Paper 
          {...props} 
          elevation={8}
          sx={{
            mt: 0.5,
            borderRadius: 2,
            '& .MuiAutocomplete-listbox': {
              py: 0
            }
          }}
        />
      )}
      clearOnBlur={false}
      clearOnEscape
      blurOnSelect
      selectOnFocus
      handleHomeEndKeys
    />
  );
});

ItemSearchField.displayName = 'ItemSearchField';

export default ItemSearchField;
